import * as amplitude from '@amplitude/analytics-browser'
import { sessionReplayPlugin } from '@amplitude/plugin-session-replay-browser'

import { flatten } from '~/utils/flatten'
import { replaceBigIntsWithStringsInObject } from '~/utils/replaceBigIntsWithStrings'

type UserProperties = {
  [key: string]: string | boolean | number | string[] | number[]
}

type TrackingData =
  | string
  | boolean
  | number
  | undefined
  | string[]
  | boolean[]
  | number[]
  | object
  | object[]

export type TrackingProperties = {
  [key: string]:
    | TrackingData
    | {
        [key: string]: TrackingData
      }
}

let isInitialized = false

export const initTracking = (): void => {
  if (process.env.APP_ENV === 'development') {
    // Have amplitude disabled locally by default
    return
  }

  const amplitudeKey = process.env.AMPLITUDE_API_KEY
  if (!amplitudeKey) return

  // sample rate is controlled through the amplitude dashboard
  const sessionReplayTracking = sessionReplayPlugin()
  amplitude.add(sessionReplayTracking)
  amplitude.init(amplitudeKey, {
    autocapture: true,
  })
  isInitialized = true
}

export const setTrackingUserProperties = (properties: UserProperties): void => {
  if (!isInitialized) return

  const identify = new amplitude.Identify()
  Object.keys(properties).forEach((key) => {
    identify.set(key, properties[key])
  })
  amplitude.identify(identify)

  if ('userId' in properties && properties.userId)
    setTrackingId(properties.userId as string)
}

const setTrackingId = (id: string): void => {
  if (!isInitialized) return

  if (id === 'undefined') amplitude.setUserId(undefined)
  else amplitude.setUserId(id)
}

const prepareEventProperties = (
  eventProperties: TrackingProperties,
): TrackingProperties => {
  return replaceBigIntsWithStringsInObject(
    flatten(eventProperties, { safe: true }),
  ) as TrackingProperties
}

export const sendTrackingData = (
  eventType: string,
  eventProperties?: TrackingProperties,
): void => {
  if (!isInitialized) return
  amplitude.track(
    eventType,
    eventProperties ? prepareEventProperties(eventProperties) : undefined,
  )
}

export const isSplitsEmail = (email: string) => {
  return email.endsWith('@splits.org')
}
